// items-math.js — Probe item bank: MATHEMATICS (calibration domain)
// =============================================================================
// One probe question per math node, keyed to the REAL registry node ids in
// graph-data.js. This domain is the calibration set: right/wrong is
// unambiguous, so it's where we tune the engine's *feel* before moving to the
// frontier nodes where "correct" gets fuzzy.
//
// AUTHORING STANDARD (every probe must satisfy all five):
//   1. DIAGNOSTIC, not trivia. A correct answer should require actually
//      understanding the node's core idea — not recall of a name or a lucky
//      guess. If someone could get it right by pattern-matching the phrasing,
//      it's a bad probe.
//   2. ONE concept. Each probe targets the single most load-bearing idea of the
//      node, so a miss cleanly implicates THAT node (and, via the DAG, its
//      descendants) rather than being ambiguous about what failed.
//   3. PREREQUISITE-CLEAN. Answering must not secretly require a HARDER topic.
//      A calculus probe must not need differential equations. Otherwise the
//      monotonic DAG propagation draws a false inference.
//   4. DISTRACTORS ARE DIAGNOSTIC. The wrong options encode the specific,
//      common misconception for that topic — so a wrong answer is informative,
//      not random noise. (Noted per item in `trap`.)
//   5. STABLE & UNAMBIGUOUS. Exactly one defensible answer; no "well, it
//      depends." Numeric where possible so there's zero interpretive wiggle.
//
// Schema per item (merged onto the registry node at load time):
//   { q, opts:[4], a:index, concept, trap }  — a = index of the correct option.
//   concept = the one idea being tested (for authoring review + future tutor).
//   trap    = what choosing a wrong distractor reveals (why the miss is
//             diagnostic). Not shown to the learner; it's authoring rationale
//             and a hook for the Phase-3 tutor's feedback.
//
// Nodes without an item set nothing here and are placed structurally via
// propagation only (the engine skips them as questions).
// =============================================================================

export const MATH_ITEMS = {

  // --- ROOT ------------------------------------------------------------------
  arithmetic: {
    q: 'Which expression equals \u00be + \u215b ?',        // 3/4 + 1/8
    opts: ['\u2077\u2044\u2088 (7/8)', '\u2074\u2044\u2081\u2082 (4/12)', '\u00bd (1/2)', '\u2074\u2044\u2081\u2081 (4/11)'],
    a: 0,
    concept: 'Fractions require a common denominator before adding; you add numerators, not denominators.',
    trap: 'Picking 4/12 or 4/11 = the classic "add tops and bottoms" error, the single most common arithmetic misconception. A miss here should cap belief in ALL of math.'
  },

  // --- FIRST TIER (prereq: arithmetic) ---------------------------------------
  algebra: {
    q: 'Solve for x:  2x \u2212 6 = 10',
    opts: ['x = 2', 'x = 8', 'x = 4', 'x = 16'],
    a: 1,
    concept: 'Isolating a variable by inverse operations: add 6, then divide by 2.',
    trap: 'x = 2 comes from dividing before adding (order-of-operations slip); x = 16 from adding 6 to the wrong side. Both reveal a real gap in symbolic manipulation, not a typo.'
  },
  geometry: {
    q: 'A right triangle has legs of length 3 and 4. How long is the hypotenuse?',
    opts: ['5', '7', '6', '\u221a7 \u2248 2.6'],
    a: 0,
    concept: 'The Pythagorean theorem: c\u00b2 = a\u00b2 + b\u00b2, so c = \u221a(9+16) = 5.',
    trap: 'Picking 7 = adding the legs (3+4) instead of squaring; a signal the person knows the shape-words but not the metric relationship. Kept prereq-clean: needs only arithmetic + the theorem.'
  },
  probability: {
    q: 'You flip two fair coins. What is the probability that BOTH land heads?',
    opts: ['1/2', '1/3', '1/4', '2/3'],
    a: 2,
    concept: 'Independent events multiply: P(H)\u00d7P(H) = \u00bd \u00d7 \u00bd = \u00bc.',
    trap: '1/3 = the classic "three outcomes (HH, HT/TH, TT) so 1/3" error that ignores ordering; 1/2 = treating the two flips as one event. Both are textbook probability misconceptions.'
  },

  // --- SECOND TIER -----------------------------------------------------------
  trigonometry: {           // prereq: geometry (hard), algebra (hard)
    q: 'What is the exact value of sin(30\u00b0)?',
    opts: ['1', '\u221a3\u20442', '1\u20442', '0'],
    a: 2,
    concept: 'The sine of 30\u00b0 is the ratio opposite/hypotenuse in a 30-60-90 triangle = 1/2.',
    trap: '\u221a3/2 is cos(30\u00b0) \u2014 choosing it reveals the very common sin/cos swap on the special angles, which is exactly the trig-specific error we want to detect.'
  },
  linear_algebra: {         // prereq: algebra (hard)
    q: 'Two nonzero vectors are perpendicular (orthogonal) exactly when their dot product is\u2026',
    opts: ['equal to 1', 'zero', 'equal to their combined length', 'undefined'],
    a: 1,
    concept: 'Orthogonality \u21d4 dot product = 0 (cos 90\u00b0 = 0). The dot product measures alignment.',
    trap: 'Picking 1 = confusing orthogonal with a unit/normalized relationship. Tests the geometric MEANING of the dot product, not matrix mechanics \u2014 kept clean of anything downstream.'
  },

  // --- THIRD TIER (hubs) -----------------------------------------------------
  calculus: {               // prereq: algebra (hard), trigonometry (soft)
    q: 'What is the derivative of  f(x) = x\u00b3 ?',
    opts: ['3x', 'x\u00b2', '3x\u00b2', 'x\u2074\u20444'],
    a: 2,
    concept: 'The power rule: d/dx[x\u207f] = n\u00b7x\u207f\u207b\u00b9, so d/dx[x\u00b3] = 3x\u00b2.',
    trap: 'x\u2074/4 is the ANTIderivative (integral) \u2014 picking it reveals the derivative/integral direction confusion, the single most common calculus error. 3x = forgetting to keep the reduced exponent.'
  },
  statistics: {             // prereq: probability (hard), calculus (soft)
    q: 'A p-value of 0.03 is the probability of\u2026',
    opts: [
      'the null hypothesis being true',
      'observing data this extreme (or more) IF the null hypothesis is true',
      'the result replicating',
      'the alternative hypothesis being true'
    ],
    a: 1,
    concept: 'A p-value is P(data this extreme | null true) \u2014 a statement about the data under an assumption, NOT the probability that a hypothesis is true.',
    trap: '"Null being true" is the single most widespread statistical misconception (the inverse-probability fallacy), held even by many practitioners. Detecting it is high-value; it separates real statistical literacy from surface familiarity.'
  },
};

// Convenience: attach items to registry node records.
// Usage in the diagnostic: node.question = MATH_ITEMS[node.id] || null;
export function attachMathItems(nodes) {
  return nodes.map(n => (MATH_ITEMS[n.id] ? { ...n, question: MATH_ITEMS[n.id] } : n));
}
